import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  Typography,
  Grid,
  makeStyles,
} from '@material-ui/core';
import { useSharedStyles } from './styles/shared';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import BlockIcon from '@material-ui/icons/Block';
import { AnalysisSession, AnalysisJobStatus } from './types';
import * as api from './api/api';

interface AnalysisProgressProps {
  session: AnalysisSession | null;
  onCancel: () => void;
  onComplete: () => void;
  onSessionUpdate?: (session: AnalysisSession) => void;
  onError?: (error: string) => void;
}

// statusIconMap is defined inside the component — see below.

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  summaryGrid: {
    marginBottom: theme.spacing(3),
  },
  summaryPaper: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
  },
  summaryLabel: {
    marginBottom: theme.spacing(1),
  },
  progressBarWrapper: {
    flex: 1,
  },
  progressCount: {
    minWidth: 60,
  },
  chipRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
  },
  headerCell: {
    fontWeight: 'bold',
  },
  jobProgressCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  jobProgressBar: {
    width: 100,
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  cancelButton: {
    borderColor: theme.palette.error.main,
  },
  iconSuccess: {
    color: theme.palette.success.main,
  },
  iconError: {
    color: theme.palette.error.main,
  },
  stepFailed: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  jobError: {
    display: 'block',
    color: theme.palette.error.main,
  },
}));

const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  session,
  onCancel,
  onComplete,
  onSessionUpdate,
  onError,
}) => {
  const { t } = useTranslation('requirement-doctor');
  const shared = useSharedStyles();
  const classes = useStyles();
  const statusIconMap: Record<AnalysisJobStatus, React.ReactNode> = {
    pending: <CircularProgress size={20} />,
    running: <CircularProgress size={20} />,
    completed: <CheckCircleIcon className={classes.iconSuccess} />,
    failed: <ErrorIcon className={classes.iconError} />,
    cancelled: <BlockIcon color="disabled" />,
  };
  const jobStatusLabel: Record<AnalysisJobStatus, string> = {
    pending: t('s2.jobStatuses.pending'),
    running: t('s2.jobStatuses.running'),
    completed: t('s2.jobStatuses.completed'),
    failed: t('s2.jobStatuses.failed'),
    cancelled: t('s2.jobStatuses.cancelled'),
  };
  const [isPolling, setIsPolling] = useState(false);

  // Keep the latest session/callbacks in refs so the polling interval (keyed
  // only on session.id) always reads current values without being torn down
  // and recreated on every update — which would reset the 2s cadence.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onSessionUpdateRef = useRef(onSessionUpdate);
  onSessionUpdateRef.current = onSessionUpdate;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // A session with at least one job, all of which reached a terminal state, is
  // "complete". A cancelled job is terminal too: it will never run again, so
  // leaving it out kept the screen polling a session that had stopped.
  // The job-count guard prevents an empty/0-job session from being treated as
  // complete (jobs.every() is vacuously true on an empty array).
  const allDone = useMemo(
    () =>
      !!session &&
      session.jobs.length > 0 &&
      session.jobs.every(
        j =>
          j.status === 'completed' ||
          j.status === 'failed' ||
          j.status === 'cancelled',
      ),
    [session],
  );

  // Fire onComplete once the session is fully done (and not cancelled).
  // onComplete is read from a ref so changing the parent's callback reference
  // (e.g. on re-render) does not restart the 1-second timer.
  useEffect(() => {
    if (!allDone || session?.cancelledAt) return undefined;
    const timer = setTimeout(() => onCompleteRef.current(), 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone, session?.cancelledAt]);

  // Poll the backend for session updates. Keyed on the session id (and whether
  // it's done/cancelled) so the interval is created once per session and lives
  // at a stable 2s cadence.
  const sessionId = session?.id;
  useEffect(() => {
    if (!sessionId || allDone || session?.cancelledAt) {
      setIsPolling(false);
      return undefined;
    }

    setIsPolling(true);
    // Prevent concurrent in-flight requests: skip a tick if the previous fetch
    // has not yet resolved (e.g. slow network, backend queued).
    const inFlight = { value: false };
    const interval = setInterval(async () => {
      if (inFlight.value) return;
      inFlight.value = true;
      try {
        const updated = await api.getAnalysisSession(sessionId);
        const current = sessionRef.current;
        if (onSessionUpdateRef.current && current) {
          onSessionUpdateRef.current({
            ...current,
            // Propagate session-level cancellation so the UI stops polling.
            cancelledAt:
              updated.status === 'SESSION_STATUS_CANCELLED'
                ? current.cancelledAt ?? new Date().toISOString()
                : current.cancelledAt,
            jobs: updated.jobs.map(job => {
              const existing = current.jobs.find(j => j.id === job.id);
              return {
                id: job.id,
                requirementId:
                  existing?.requirementId ?? `RQ-${job.requirementVersionId}`,
                step: job.step,
                status: api.normalizeJobStatus(job.status),
                progress: job.progress,
                // Preserve the original startedAt rather than re-stamping on every poll.
                startedAt:
                  job.startedAt ?? existing?.startedAt ?? current.startedAt,
                completedAt: job.completedAt ?? existing?.completedAt,
                error: job.errorMessage ?? existing?.error,
              };
            }),
          });
        }
      } catch (error) {
        console.error('Failed to poll session:', error);
        if (onErrorRef.current) {
          onErrorRef.current(
            error instanceof Error ? error.message : 'Failed to poll session',
          );
        }
      } finally {
        inFlight.value = false;
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // sessionRef / onSessionUpdateRef / onErrorRef are refs — intentionally
    // omitted so the interval is not torn down and recreated on every update.
  }, [sessionId, allDone, session?.cancelledAt]);

  if (!session) {
    return null;
  }

  const completedCount = session.jobs.filter(
    j => j.status === 'completed',
  ).length;
  const totalCount = session.totalRequirements;
  const overallProgress =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isRunning =
    !session.cancelledAt &&
    session.jobs.some(j => j.status === 'pending' || j.status === 'running');
  let sessionStatus = t('s2.statusChips.completed');
  if (session.cancelledAt) {
    sessionStatus = t('s2.statusChips.cancelled');
  } else if (isRunning) {
    sessionStatus = t('s2.statusChips.running');
  }

  return (
    <Box className={shared.root}>
      <Typography variant="h6" className={classes.title}>
        {t('s2.title')}
      </Typography>
      {isPolling && (
        <Alert severity="info" className={classes.alert}>
          {t('s2.messages.polling')}
        </Alert>
      )}

      <Grid container spacing={2} className={classes.summaryGrid}>
        <Grid item xs={12} sm={6}>
          <Paper className={classes.summaryPaper}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.summaryLabel}
            >
              {t('s2.overallProgress')}
            </Typography>
            <Box className={shared.progressRow}>
              <Box className={classes.progressBarWrapper}>
                <LinearProgress variant="determinate" value={overallProgress} />
              </Box>
              <Typography variant="body2" className={classes.progressCount}>
                {completedCount}/{totalCount}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper className={classes.summaryPaper}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.summaryLabel}
            >
              {t('s2.sessionInfo')}
            </Typography>
            <Box className={classes.chipRow}>
              <Chip
                size="small"
                label={t('s2.statusLabel', { status: sessionStatus })}
                color={isRunning ? 'primary' : 'default'}
                className={
                  isRunning || session.cancelledAt
                    ? undefined
                    : shared.chipSuccess
                }
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${t('s2.messages.started', {
                  time: new Date(session.startedAt).toLocaleTimeString(),
                })}`}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table size="small">
          <TableHead className={shared.tableHead}>
            <TableRow>
              <TableCell className={classes.headerCell}>
                {t('s2.table.columns.requirement')}
              </TableCell>
              <TableCell className={classes.headerCell}>
                {t('s2.table.columns.step')}
              </TableCell>
              <TableCell className={classes.headerCell} align="center">
                {t('s2.table.columns.progress')}
              </TableCell>
              <TableCell className={classes.headerCell} align="center">
                {t('s2.table.columns.status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {session.jobs.map(job => {
              const chipColor =
                job.status === 'running' ? 'primary' : 'default';
              const chipClassByStatus: Partial<
                Record<AnalysisJobStatus, string>
              > = {
                completed: shared.chipSuccess,
                failed: shared.chipError,
              };
              const chipClass = chipClassByStatus[job.status];
              return (
                <TableRow key={job.id} hover>
                  <TableCell>{job.requirementId}</TableCell>
                  <TableCell>
                    <span
                      className={
                        job.status === 'failed' ? classes.stepFailed : undefined
                      }
                    >
                      {job.step}
                    </span>
                    {/* Why it failed, so a failed requirement is not left as a
                        bare red chip among the ones that worked. */}
                    {job.status === 'failed' && job.error && (
                      <Typography
                        variant="caption"
                        className={classes.jobError}
                      >
                        ⚠ {job.error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box className={classes.jobProgressCell}>
                      <LinearProgress
                        variant="determinate"
                        value={job.progress}
                        className={classes.jobProgressBar}
                      />
                      <Typography variant="caption">{job.progress}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box className={classes.statusCell}>
                      {statusIconMap[job.status]}
                      <Chip
                        size="small"
                        label={jobStatusLabel[job.status]}
                        color={chipColor}
                        className={chipClass}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.actions}>
        <Button
          variant="outlined"
          className={`${shared.cancelButton} ${classes.cancelButton}`}
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={!isRunning}
        >
          {t('s2.buttons.cancel')}
        </Button>
      </Box>
    </Box>
  );
};

export default AnalysisProgress;
